import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Send, Trophy, IndianRupee, Zap, Store as StoreIcon, ShieldCheck,
  Info, AlertTriangle, MessageSquare, Check, Bot, Settings, Brain, X,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { aiPurchase } from '../lib/api';
import MandiPrices from '../components/MandiPrices';

const PRIORITIES = [
  { id: 'balanced',      label: 'Balanced' },
  { id: 'cheapest',      label: 'Cheapest' },
  { id: 'quality',       label: 'Best quality' },
  { id: 'fastest',       label: 'Fastest delivery' },
  { id: 'local',         label: 'Nearest / local' },
  { id: 'best_warranty', label: 'Best warranty' },
];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function AiBuy() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [priority, setPriority] = useState('balanced');
  const [pincode, setPincode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Modal state
  const [detailOpt, setDetailOpt] = useState(null);   // { option } for the deep-dive drawer
  const [negotiateOpt, setNegotiateOpt] = useState(null);
  const [approveOpt, setApproveOpt] = useState(null);
  const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);

  const search = async () => {
    if (!user) { toast.error('Sign in first — the AI needs to record what you shop for.'); return; }
    if (query.trim().length < 3) { toast.error('Tell me what you need (e.g. "1 HP copper water pump under 8000")'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await aiPurchase.search({
        query: query.trim(),
        preferences: priority !== 'balanced' ? { priority } : undefined,
        pincode: pincode || undefined,
      });
      if (priority !== 'balanced') r.spec.priority = priority;
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI search failed');
    } finally { setBusy(false); }
  };

  const optionId = (o) => o ? `${o.store_id}::${o.product_id}` : null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#D35400]">AI Procurement Network</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">
            Tell me what you need — I&apos;ll <span className="text-[#E67E22]">source, verify, negotiate</span> and buy.
          </h1>
          <p className="text-sm text-ink/60 mt-1">
            10 engines: intelligence · discovery · matching · price · trust · negotiation · optimisation · risk · autonomy · learning.
          </p>
        </div>
        <button data-testid="ai-buy-auto-settings-btn" onClick={() => setAutoSettingsOpen(true)}
          className="pill !bg-white shrink-0" title="Auto-buy safety limits">
          <Settings className="w-4 h-4"/> Auto-buy
        </button>
      </div>

      <section className="mt-6 card-soft p-5">
        <textarea data-testid="ai-buy-query" rows={3}
          value={query} onChange={e => setQuery(e.target.value)}
          placeholder='e.g. "1000 bags of 50 kg cement, good brand, delivered to my construction site"'
          className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          {PRIORITIES.map(p => (
            <button key={p.id} data-testid={`ai-buy-priority-${p.id}`}
              onClick={() => setPriority(p.id)}
              className={`pill ${priority === p.id ? '!bg-[#E67E22] !text-white !border-[#E67E22]' : '!bg-white'}`}>
              {p.label}
            </button>
          ))}
          <input data-testid="ai-buy-pincode" placeholder="Your pincode (optional)"
            value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm w-40 font-mono"/>
          <button data-testid="ai-buy-search-btn" disabled={busy} onClick={search}
            className="btn-primary ml-auto">
            <Send className="w-4 h-4"/>{busy ? 'Thinking…' : 'Find best options'}
          </button>
        </div>
      </section>

      {busy && (
        <div className="mt-6 text-sm text-ink/60 inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 animate-pulse"/>Parsing, discovering, matching, scoring & risk-checking sellers…
        </div>
      )}

      {result && (
        <>
          <SpecCard spec={result.spec} weights={result.weights} learned={result.learned_applied}/>
          <Podium result={result} money={money}
            onDetail={setDetailOpt} onNegotiate={setNegotiateOpt} onApprove={setApproveOpt}/>
          <AllOptions options={result.options} groups={result.groups}
            best={result.best_overall} cheapest={result.cheapest} fastest={result.fastest}
            optionId={optionId} onDetail={setDetailOpt}
            onNegotiate={setNegotiateOpt} onApprove={setApproveOpt}/>
        </>
      )}

      <MandiPrices/>

      {detailOpt && (
        <DetailDrawer option={detailOpt} spec={result?.spec}
          onClose={() => setDetailOpt(null)}
          onNegotiate={(o) => { setDetailOpt(null); setNegotiateOpt(o); }}
          onApprove={(o) => { setDetailOpt(null); setApproveOpt(o); }}/>
      )}
      {negotiateOpt && (
        <NegotiateModal option={negotiateOpt}
          onClose={() => setNegotiateOpt(null)}
          onApproveThread={(threadId) => { setNegotiateOpt(null); setApproveOpt({ ...negotiateOpt, negotiation_id: threadId }); }}/>
      )}
      {approveOpt && (
        <ApproveModal option={approveOpt} defaultPincode={pincode}
          onClose={() => setApproveOpt(null)}/>
      )}
      {autoSettingsOpen && (
        <AutoLimitsModal onClose={() => setAutoSettingsOpen(false)}/>
      )}
    </div>
  );
}

/* ---------------- Spec card ---------------- */
function SpecCard({ spec, weights, learned }) {
  const nonZero = Object.entries(weights).filter(([, v]) => v > 0.01);
  return (
    <section className="mt-6 card-soft p-5" data-testid="ai-buy-spec-card">
      <div className="flex items-center gap-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Understood requirement</div>
        {learned && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8F5E9] text-[#1B5E20] inline-flex items-center gap-1">
            <Brain className="w-3 h-3"/> personalised from your history
          </span>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Info2 label="Product" value={spec.product}/>
        {spec.brand && <Info2 label="Brand" value={spec.brand}/>}
        <Info2 label="Quantity" value={spec.quantity}/>
        {spec.budget_inr && <Info2 label="Budget" value={`₹${spec.budget_inr}`}/>}
        <Info2 label="Priority" value={spec.priority}/>
        {spec.delivery_max_days && <Info2 label="Deliver in" value={`≤ ${spec.delivery_max_days} d`}/>}
      </div>
      {spec.specifications && Object.keys(spec.specifications).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Specs</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(spec.specifications).map(([k, v]) => (
              <span key={k} className="pill !bg-[#FFF8EE] !border-[#E67E22]/30 !text-[#7A4A00]">{k}: {String(v)}</span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-ink/50">Ranking weights</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {nonZero.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F5F5F5] text-ink/60">
              {k.replace(/_/g, ' ')} <b className="text-ink/80">{Math.round(v * 100)}%</b>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Info2({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-white p-2">
      <div className="text-[10px] uppercase tracking-widest text-ink/50">{label}</div>
      <div className="font-semibold truncate">{value ?? '—'}</div>
    </div>
  );
}

/* ---------------- Podium ---------------- */
function Podium({ result, money, onDetail, onNegotiate, onApprove }) {
  const find = (id) => result.options.find(o => `${o.store_id}::${o.product_id}` === id);
  const best = find(result.best_overall);
  const cheap = find(result.cheapest);
  const fast = find(result.fastest);
  if (!best) {
    return (
      <div className="mt-4 card-soft p-6 text-sm text-ink/60">
        {result.message || 'No sellers matched — try broader wording.'}
      </div>
    );
  }
  return (
    <section className="mt-4 grid md:grid-cols-3 gap-3" data-testid="ai-buy-podium">
      <PodiumCard tone="best"  Icon={Trophy}      title="Best overall"     opt={best}
        money={money} onDetail={onDetail} onNegotiate={onNegotiate} onApprove={onApprove}/>
      {cheap && <PodiumCard tone="cheap" Icon={IndianRupee}  title="Cheapest total" opt={cheap}
        money={money} onDetail={onDetail} onNegotiate={onNegotiate} onApprove={onApprove}/>}
      {fast  && <PodiumCard tone="fast"  Icon={Zap}          title="Fastest delivery" opt={fast}
        money={money} onDetail={onDetail} onNegotiate={onNegotiate} onApprove={onApprove}/>}
    </section>
  );
}

function PodiumCard({ tone, Icon, title, opt, money, onDetail, onNegotiate, onApprove }) {
  const border = { best: 'border-[#E67E22]', cheap: 'border-[#1B5E20]', fast: 'border-[#0D47A1]' }[tone];
  const text   = { best: 'text-[#D35400]',   cheap: 'text-[#1B5E20]',   fast: 'text-[#0D47A1]' }[tone];
  const risk = opt.risk_flags || [];
  return (
    <div data-testid={`ai-buy-podium-${tone}`}
      className={`card-soft p-4 border-l-4 ${border} flex flex-col`}>
      <div className={`inline-flex items-center gap-2 font-heading font-bold ${text}`}>
        <Icon className="w-4 h-4"/>{title}
      </div>
      <div className="mt-2 font-semibold text-lg truncate">{opt.product_name}</div>
      <div className="text-xs text-ink/60 flex items-center gap-1">
        <StoreIcon className="w-3 h-3"/>{opt.store_name}
      </div>
      <div className="mt-2 font-heading font-black text-2xl tabular-nums">{money(opt.total_cost_inr)}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink/50">
        {opt.seller_classification.replace(/_/g, ' ')} · score {opt.final_score.toFixed(1)}
      </div>
      <TrustPill trust={opt.trust}/>
      {risk.length > 0 && <RiskChips flags={risk} compact/>}
      <div className="mt-3 flex gap-1 flex-wrap">
        <button data-testid={`ai-buy-${tone}-detail`} onClick={() => onDetail(opt)}
          className="pill text-xs !bg-white"><Info className="w-3 h-3"/>Why</button>
        <button data-testid={`ai-buy-${tone}-negotiate`} onClick={() => onNegotiate(opt)}
          className="pill text-xs !bg-white"><MessageSquare className="w-3 h-3"/>Negotiate</button>
        <button data-testid={`ai-buy-${tone}-approve`} onClick={() => onApprove(opt)}
          className="pill text-xs !bg-[#E67E22] !text-white !border-[#E67E22]"><Check className="w-3 h-3"/>Approve &amp; Buy</button>
      </div>
    </div>
  );
}

function TrustPill({ trust }) {
  if (!trust) return null;
  const s = trust.score || 0;
  const tone = s >= 75 ? 'bg-[#E8F5E9] text-[#1B5E20]' : s >= 55 ? 'bg-[#FFF3E0] text-[#EF6C00]' : 'bg-[#FFEBEE] text-[#B71C1C]';
  return (
    <div className={`mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full w-fit ${tone}`}>
      <ShieldCheck className="w-3 h-3"/> trust {s.toFixed(0)}
      {trust.kyc_verified && <span className="ml-1 font-bold">KYC</span>}
    </div>
  );
}

function RiskChips({ flags, compact }) {
  if (!flags?.length) return null;
  return (
    <div className={`${compact ? 'mt-1' : 'mt-3'} flex flex-wrap gap-1`}>
      {flags.map((f, i) => {
        const cls = f.severity === 'high' ? 'bg-[#FFEBEE] text-[#B71C1C]'
                  : f.severity === 'medium' ? 'bg-[#FFF3E0] text-[#EF6C00]'
                  : 'bg-[#FFFDE7] text-[#795548]';
        return (
          <span key={i} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${cls}`}
            title={f.hint}>
            <AlertTriangle className="w-3 h-3"/>{f.code.replace(/_/g, ' ')}
          </span>
        );
      })}
    </div>
  );
}

/* ---------------- All options list ---------------- */
function AllOptions({ options, groups, best, cheapest, fastest, optionId,
                      onDetail, onNegotiate, onApprove }) {
  if (!options?.length) return null;
  const groupCount = Object.keys(groups || {}).length;
  return (
    <section className="mt-6" data-testid="ai-buy-all-options">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/50">
        All matched sellers · {options.length}
        {groupCount > 0 && <span className="ml-2 normal-case tracking-normal text-ink/40">
          ({groupCount} equivalent product group{groupCount > 1 ? 's' : ''})
        </span>}
      </div>
      <div className="mt-2 grid gap-2">
        {options.map(o => {
          const id = optionId(o);
          const tag = id === best ? 'BEST' : id === cheapest ? 'CHEAP' : id === fastest ? 'FAST' : '';
          return (
            <div key={id} data-testid={`ai-buy-opt-${o.store_id}`}
              className="card-soft p-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{o.product_name}</div>
                <div className="text-xs text-ink/60 truncate">
                  <StoreIcon className="w-3 h-3 inline mr-1"/>{o.store_name}
                  <span className="ml-2 text-[10px] uppercase tracking-wider">{o.seller_classification.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-[11px] text-ink/50 mt-1"><Info className="w-3 h-3 inline mr-1"/>{o.why}</div>
                <div className="flex items-center gap-2 mt-1">
                  <TrustPill trust={o.trust}/>
                  <RiskChips flags={o.risk_flags} compact/>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-lg tabular-nums">₹{o.total_cost_inr.toLocaleString('en-IN')}</div>
                <div className="text-[10px] uppercase tracking-widest text-ink/50">score {o.final_score.toFixed(1)}</div>
              </div>
              {tag && (
                <span className={`ml-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${
                  tag === 'BEST' ? 'bg-[#FFF7EE] text-[#D35400]'
                    : tag === 'CHEAP' ? 'bg-[#E8F5E9] text-[#1B5E20]'
                    : 'bg-[#E3F2FD] text-[#0D47A1]'
                }`}>{tag}</span>
              )}
              <div className="w-full flex gap-1 mt-1">
                <button data-testid={`ai-buy-opt-${o.store_id}-detail`} onClick={() => onDetail(o)}
                  className="pill text-xs !bg-white"><Info className="w-3 h-3"/>Why</button>
                <button data-testid={`ai-buy-opt-${o.store_id}-negotiate`} onClick={() => onNegotiate(o)}
                  className="pill text-xs !bg-white"><MessageSquare className="w-3 h-3"/>Negotiate</button>
                <button data-testid={`ai-buy-opt-${o.store_id}-approve`} onClick={() => onApprove(o)}
                  className="pill text-xs !bg-[#E67E22] !text-white !border-[#E67E22]"><Check className="w-3 h-3"/>Approve &amp; Buy</button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Detail drawer (LLM explanation) ---------------- */
function DetailDrawer({ option, spec, onClose, onNegotiate, onApprove }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await aiPurchase.explain(option, spec);
      setText(r.explanation || '');
    } catch (e) {
      setText('Unable to fetch explanation right now.');
    } finally { setLoading(false); }
  }, [option, spec]);

  useEffect(() => { load(); }, [load]);

  const c = option.cost_breakdown || {};
  const risk = option.risk_flags || [];
  return (
    <ModalShell title="AI recommendation deep-dive" onClose={onClose} testid="ai-buy-detail-drawer">
      <div className="text-sm text-ink/70">
        <div className="font-heading font-bold text-lg text-ink">{option.product_name}</div>
        <div className="text-xs text-ink/60"><StoreIcon className="w-3 h-3 inline mr-1"/>{option.store_name} · {option.seller_classification.replace(/_/g, ' ')}</div>
      </div>
      <div className="mt-3 rounded-lg border border-border bg-[#FFFCF7] p-3 text-sm text-ink/80 min-h-[4rem]">
        {loading ? <span className="inline-flex items-center gap-2"><Bot className="w-4 h-4 animate-pulse"/>AI is drafting your explanation…</span> : text}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <Kv k="Delivered total" v={money(option.total_cost_inr)}/>
        <Kv k="Unit price" v={money(option.unit_price_inr)}/>
        <Kv k="Subtotal" v={money(c.subtotal)}/>
        <Kv k="Shipping" v={money(c.shipping)}/>
        <Kv k="Tax" v={money(c.tax)}/>
        <Kv k="Discount" v={c.discount ? `– ${money(c.discount)}` : '—'}/>
      </div>
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-ink/50">Trust breakdown</div>
        <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
          <Kv k="Composite" v={`${(option.trust?.score || 0).toFixed(1)}/100`}/>
          <Kv k="KYC verified" v={option.trust?.kyc_verified ? 'Yes' : 'No'}/>
          <Kv k="Reputation" v={`${option.trust?.reputation || 0}/100`}/>
          <Kv k="Delivered %" v={`${option.trust?.delivered_pct || 0}%`}/>
          <Kv k="Network role" v={option.trust?.network_role || '—'}/>
          <Kv k="MS-ID" v={option.trust?.ms_id || '—'}/>
        </div>
      </div>
      {risk.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-[#B71C1C]">Risk signals</div>
          <ul className="mt-1 space-y-1 text-xs text-ink/70">
            {risk.map((f, i) => <li key={i}><b className="uppercase tracking-wider text-[10px]">{f.severity}</b> · {f.hint}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-4 flex gap-2 justify-end">
        <button className="pill !bg-white" onClick={() => onNegotiate(option)}><MessageSquare className="w-3 h-3"/>Negotiate</button>
        <button className="btn-primary" onClick={() => onApprove(option)}><Check className="w-3 h-3"/>Approve &amp; Buy</button>
      </div>
    </ModalShell>
  );
}

function Kv({ k, v }) {
  return (
    <div className="rounded-lg border border-border bg-white p-2">
      <div className="text-[10px] uppercase tracking-widest text-ink/50">{k}</div>
      <div className="font-semibold text-ink text-sm">{v}</div>
    </div>
  );
}

/* ---------------- Negotiation modal ---------------- */
function NegotiateModal({ option, onClose, onApproveThread }) {
  const [target, setTarget] = useState(Math.round((option.unit_price_inr || 0) * 0.9));
  const [qty, setQty] = useState(option.quantity || 1);
  const [note, setNote] = useState('');
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!target || target <= 0) { toast.error('Enter a valid target price'); return; }
    setBusy(true);
    try {
      const t = await aiPurchase.negotiate({
        option_id: `${option.store_id}::${option.product_id}`,
        target_price_inr: Number(target),
        quantity: Number(qty),
        note,
      });
      setThread(t);
      if (t.status === 'agreed_auto') toast.success('Seller auto-accepted your offer!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Negotiation failed');
    } finally { setBusy(false); }
  };

  const status = thread?.status;
  const agreed = thread?.agreed_price_inr;
  return (
    <ModalShell title={`Negotiate with ${option.store_name}`} onClose={onClose} testid="ai-buy-negotiate-modal">
      <div className="text-xs text-ink/60 mb-2">
        Listed price: <b>{money(option.unit_price_inr)}</b> · Ask for {qty} unit(s).
      </div>
      {!thread && (
        <div className="grid gap-2">
          <label className="text-xs uppercase tracking-widest text-ink/50">Your target unit price (₹)</label>
          <input data-testid="ai-buy-negotiate-target" type="number"
            value={target} onChange={e => setTarget(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <label className="text-xs uppercase tracking-widest text-ink/50">Quantity</label>
          <input data-testid="ai-buy-negotiate-qty" type="number" min={1}
            value={qty} onChange={e => setQty(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <label className="text-xs uppercase tracking-widest text-ink/50">Note to seller (optional)</label>
          <textarea data-testid="ai-buy-negotiate-note" rows={2}
            value={note} onChange={e => setNote(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <button data-testid="ai-buy-negotiate-submit"
            disabled={busy} onClick={start} className="btn-primary mt-2">
            {busy ? 'Sending…' : 'Send offer'}
          </button>
        </div>
      )}
      {thread && (
        <div>
          <div className={`text-xs px-2 py-1 rounded-full inline-block ${
            status === 'agreed_auto' || status === 'agreed'
              ? 'bg-[#E8F5E9] text-[#1B5E20]'
              : status === 'declined'
                ? 'bg-[#FFEBEE] text-[#B71C1C]'
                : 'bg-[#FFF3E0] text-[#EF6C00]'
          }`}>{status?.replace(/_/g, ' ') || 'pending'}</div>
          {agreed && <div className="mt-2 text-sm">Agreed unit price: <b>{money(agreed)}</b> ({money(agreed * qty)} total)</div>}
          <div className="mt-3 space-y-2">
            {(thread.messages || []).map(m => (
              <div key={m.id} className="text-xs border border-border rounded-lg p-2 bg-white">
                <div className="uppercase tracking-widest text-[10px] text-ink/50">{m.from} · {m.type}</div>
                {m.unit_price_inr && <div><b>{money(m.unit_price_inr)}</b>/unit</div>}
                {m.note && <div className="text-ink/70">{m.note}</div>}
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            {(status === 'agreed' || status === 'agreed_auto') && (
              <button data-testid="ai-buy-negotiate-approve"
                className="btn-primary" onClick={() => onApproveThread(thread.id)}>
                <Check className="w-3 h-3"/>Approve &amp; Buy at {money(agreed)}
              </button>
            )}
            <button className="pill !bg-white" onClick={onClose}>Close</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/* ---------------- Approve modal (creates order) ---------------- */
function ApproveModal({ option, defaultPincode, onClose }) {
  const [addr, setAddr] = useState('');
  const [pin, setPin] = useState(defaultPincode || '');
  const [pay, setPay] = useState('cod');
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const submit = async () => {
    if (!addr || addr.length < 3) { toast.error('Enter your delivery address'); return; }
    if (!/^\d{6}$/.test(pin)) { toast.error('Enter a valid 6-digit pincode'); return; }
    setBusy(true);
    try {
      const body = {
        address_line: addr, pincode: pin,
        payment_method: pay, quantity: option.quantity || 1,
      };
      if (option.negotiation_id) body.negotiation_id = option.negotiation_id;
      else body.option_id = `${option.store_id}::${option.product_id}`;
      const r = await aiPurchase.approve(body);
      setOrder(r.order);
      toast.success('Order placed by AI agent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place order');
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Approve & auto-place order" onClose={onClose} testid="ai-buy-approve-modal">
      {!order && (
        <div className="grid gap-2">
          <div className="text-sm">
            Buying <b>{option.product_name}</b> from <b>{option.store_name}</b>
            {option.negotiation_id && <span className="ml-1 text-[#1B5E20]">(agreed price)</span>}
          </div>
          <label className="text-xs uppercase tracking-widest text-ink/50">Delivery address</label>
          <textarea data-testid="ai-buy-approve-address" rows={2} value={addr}
            onChange={e => setAddr(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <label className="text-xs uppercase tracking-widest text-ink/50">Pincode</label>
          <input data-testid="ai-buy-approve-pincode" value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm w-40 font-mono"/>
          <label className="text-xs uppercase tracking-widest text-ink/50">Payment</label>
          <select data-testid="ai-buy-approve-pay" value={pay} onChange={e => setPay(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
            <option value="cod">Cash on delivery</option>
            <option value="online">Online payment</option>
            <option value="khata">Digital Khata (credit)</option>
            <option value="whatsapp">Owner will confirm on WhatsApp</option>
          </select>
          <button data-testid="ai-buy-approve-submit" onClick={submit} disabled={busy}
            className="btn-primary mt-2">
            {busy ? 'Placing…' : 'Place order'}
          </button>
        </div>
      )}
      {order && (
        <div className="text-sm">
          <div className="text-[#1B5E20] font-heading font-bold text-lg">Order placed!</div>
          <div className="mt-2">Order ID: <span className="font-mono text-xs">{order.id}</span></div>
          <div>Total: <b>{money(order.total)}</b> · Payment: {order.payment_method}</div>
          <div className="mt-4 flex justify-end">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/* ---------------- Auto-buy limits ---------------- */
function AutoLimitsModal({ onClose }) {
  const [state, setState] = useState({
    enabled: false, max_spend_per_order_inr: 0, max_spend_per_month_inr: 0,
    min_trust_score: 70, min_reliability: 60, priority: 'balanced',
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aiPurchase.getAutoLimits().then(setState).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await aiPurchase.putAutoLimits(state);
      setState(r);
      toast.success('Auto-buy settings saved');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const set = (k, v) => setState(s => ({ ...s, [k]: v }));
  return (
    <ModalShell title="AI Auto-buy safety limits" onClose={onClose} testid="ai-buy-auto-modal">
      {loading ? 'Loading…' : (
        <div className="grid gap-3 text-sm">
          <div className="text-xs text-ink/60">
            When enabled, BharatCart&apos;s AI can autonomously buy on your behalf — but only within
            the limits you set here. It never places an order if any &apos;high&apos; risk flag is raised.
          </div>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="ai-buy-auto-enabled"
              checked={!!state.enabled} onChange={e => set('enabled', e.target.checked)}/>
            Enable AI Auto-buy
          </label>
          <Row label="Max spend per order (₹)">
            <input type="number" data-testid="ai-buy-auto-max-order" value={state.max_spend_per_order_inr}
              onChange={e => set('max_spend_per_order_inr', Number(e.target.value))}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm w-40"/>
          </Row>
          <Row label="Max spend per month (₹)">
            <input type="number" data-testid="ai-buy-auto-max-month" value={state.max_spend_per_month_inr}
              onChange={e => set('max_spend_per_month_inr', Number(e.target.value))}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm w-40"/>
          </Row>
          <Row label="Minimum seller trust score">
            <input type="number" data-testid="ai-buy-auto-min-trust" value={state.min_trust_score}
              onChange={e => set('min_trust_score', Number(e.target.value))}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm w-24"/>
          </Row>
          <Row label="Minimum seller reliability">
            <input type="number" data-testid="ai-buy-auto-min-rel" value={state.min_reliability}
              onChange={e => set('min_reliability', Number(e.target.value))}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm w-24"/>
          </Row>
          <Row label="Priority">
            <select data-testid="ai-buy-auto-priority" value={state.priority}
              onChange={e => set('priority', e.target.value)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
              {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Row>
          <div className="flex justify-end gap-2 mt-2">
            <button className="pill !bg-white" onClick={onClose}>Cancel</button>
            <button data-testid="ai-buy-auto-save" className="btn-primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save limits'}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink/70">{label}</span>
      {children}
    </div>
  );
}

/* ---------------- Modal shell ---------------- */
function ModalShell({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4"
      data-testid={testid} onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white border border-border shadow-xl max-h-[90vh] overflow-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-heading font-bold text-ink">{title}</div>
          <button className="text-ink/60 hover:text-ink" onClick={onClose} data-testid={`${testid}-close`}>
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
